import { getUserAndTokenFromStorage } from '@firsttable/functions';
import { isNextProductionBuild, isStellateEndpoint } from '@firsttable/common';
import { isServer } from '@discover-lib/utils';
import Logger from '@/lib/logger';

const LOG_ENABLED = false;
const logger = new Logger('GET_HEADERS', 'LOG');
if (LOG_ENABLED) {
  logger.enable();
}

const PROFILE = !isServer() && false;

export default async function getHeaders(requestId: string, headersInit: RequestInit['headers']) {
  if (PROFILE) {
    performance.mark('getHeaders.start');
  }

  if (LOG_ENABLED) {
    logger.log(`${requestId} - Preparing headers`, { isServer: isServer() });
  }

  const headers = new Headers(headersInit);
  headers.set('Content-Type', 'application/json');

  // Add CMS headers
  const apiStage = process.env.NEXT_PUBLIC_GRAPHQL_API_STAGE;
  if (!apiStage) {
    throw new Error('"stage" header not set');
  }
  headers.set('stage', apiStage);
  if (process.env.NEXT_PUBLIC_WEBSITE_URL) {
    headers.set('origin', process.env.NEXT_PUBLIC_WEBSITE_URL);
  }

  // Add authorization headers
  if (!isServer()) {
    const { token, user } = getUserAndTokenFromStorage();
    if (user?.isLoggedIn && token) {
      if (LOG_ENABLED) {
        logger.log(`${requestId} - Adding auth token`, {
          userId: user.id,
          tokenPreview: token ? `${token.slice(0, 4)}...${token.slice(-4)}` : undefined,
        });
      }
      headers.set('Application-Authorization', `Bearer ${token}`);
    } else if (LOG_ENABLED) {
      logger.log(`${requestId} - No auth token available`, {
        hasUser: !!user,
        isLoggedIn: !!user?.isLoggedIn,
        hasToken: !!token,
      });
    }
  }

  // Add Stellate tracking headers
  headers.set('x-graphql-client-name', 'Website');
  if (process.env.NEXT_PUBLIC_WEBSITE_URL) {
    headers.set('x-graphql-client-version', process.env.NEXT_PUBLIC_WEBSITE_URL);
  }

  // Build-only Stellate quota. Request-time SSR uses the end-user rate-limit rules.
  const graphqlUrl =
    process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT || process.env.STORYBOOK_GRAPHQL_API_URL;
  if (
    isServer() &&
    isNextProductionBuild() &&
    process.env.STELLATE_RATE_LIMITING_SHARED_SECRET &&
    graphqlUrl &&
    isStellateEndpoint(graphqlUrl)
  ) {
    headers.set('x-graphql-rate-limiting-secret', process.env.STELLATE_RATE_LIMITING_SHARED_SECRET);
  }

  if (PROFILE) {
    performance.mark('getHeaders.end');

    performance.measure('getHeaders', {
      start: 'getHeaders.start',
      end: 'getHeaders.end',
    });
  }

  if (LOG_ENABLED) {
    logger.log(`${requestId} - Headers prepared`);
  }

  return headers;
}
